/**
 * Represents a phone number, storing information such as dial code, region code, and significant digits.
 */
export class PhoneNumber {
    /**
     * Constructs a new PhoneNumber object.
     * @param dialCode - The dial code of the phone number (e.g., "+1").
     * @param regionCode - The region code of the phone number (e.g., "US").
     * @param significantDigits - The significant digits of the phone number (e.g., "4155552671").
     * @param isValid - A boolean indicating if the phone number is valid.
     * @param displayNumber - An optional formatted display version of the phone number (e.g., "(415) 555-2671").
     */
    // eslint-disable-next-line no-useless-constructor
    public constructor(
        private readonly dialCode: string,
        private readonly regionCode: string,
        private readonly significantDigits: string,
        private readonly isValid: boolean,
        private readonly displayNumber?: string,
    ) {}

    /**
     * Returns the full phone number, including the dial code and significant digits.
     * @returns The full phone number as a string.
     */
    public getFullNumber(): string {
        return this.dialCode + this.significantDigits;
    }

    /**
     * Returns the display version of the phone number, or the significant digits if a display version is not available.
     * @returns The display version of the phone number as a string.
     */
    public getDisplayNumber(): string {
        return this.displayNumber ?? this.significantDigits;
    }

    /**
     * Returns the dial code of the phone number.
     * @returns The dial code as a string.
     */
    public getDialCode(): string {
        return this.dialCode;
    }

    /**
     * Returns whether the phone number is valid.
     * @returns A boolean indicating if the phone number is valid.
     */
    public getIsValid(): boolean {
        return this.isValid;
    }

    /**
     * Returns the region code of the phone number.
     * @returns The region code as a string.
     */
    public getRegionCode(): string {
        return this.regionCode;
    }

    /**
     * Returns the significant digits of the phone number.
     * @returns The significant digits as a string.
     */
    public getSignificant(): string {
        return this.significantDigits;
    }
}
